import { jsx, jsxs } from "react/jsx-runtime";
import { PromptInput, PromptInputBody, PromptInputTextarea, usePromptInputAttachments, PromptInputAttachments, PromptInputAttachment, PromptInputSpeechButton } from "../../ai-elements/prompt-input.js";
import { ChatInput } from "@brandclub/bc-widgets";
import { IconImageUpload, IconMicrophone, IconArrowRight } from "./ConciergeIcons.js";
import { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const getFontWeightValue = (weight) => {
  const weightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return weightMap[weight] || 400;
};
const PromptInputConcierge = ({
  placeholder = "Ask anything",
  onSubmit,
  disabled = false,
  isStreaming = false,
  value,
  onChange,
  expandDirection = "up",
  maxHeight
}) => {
  const textareaRef = useRef(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const checkMultiline = () => {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 21;
      const scrollHeight = textarea.scrollHeight;
      const paddingTop = parseInt(getComputedStyle(textarea).paddingTop) || 0;
      const paddingBottom = parseInt(getComputedStyle(textarea).paddingBottom) || 0;
      const contentHeight = scrollHeight - paddingTop - paddingBottom;
      setIsMultiline(contentHeight > lineHeight * 1.5);
    };
    checkMultiline();
    const observer = new ResizeObserver(checkMultiline);
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [value]);
  const handleSubmit = useCallback(
    async (message) => {
      var _a;
      if (!((_a = message.text) == null ? void 0 : _a.trim()) && (!message.files || message.files.length === 0)) {
        return;
      }
      let imageData;
      let imageMimeType;
      if (message.files && message.files.length > 0) {
        const file = message.files[0];
        if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || "")) {
          toast.error("Please upload a valid image file (JPEG, PNG, WebP, or GIF)");
          return;
        }
        imageMimeType = file.mediaType;
        if (file.url) {
          if (file.url.startsWith("data:")) {
            const dataUrlPrefix = "data:";
            const base64Prefix = ";base64,";
            try {
              if (file.url.includes(base64Prefix)) {
                const mimeTypeEnd = file.url.indexOf(base64Prefix);
                imageMimeType = file.url.substring(dataUrlPrefix.length, mimeTypeEnd);
                imageData = file.url.substring(mimeTypeEnd + base64Prefix.length);
              } else {
                console.error("Data URL doesn't contain base64 marker");
              }
            } catch (error) {
              console.error("Failed to extract base64 from data URL:", error);
            }
          } else if (file.url.startsWith("blob:")) {
            try {
              const response = await fetch(file.url);
              const blob = await response.blob();
              const reader = new FileReader();
              imageData = await new Promise((resolve, reject) => {
                reader.onloadend = () => {
                  const result = reader.result;
                  const base64Match = result.match(/^data:[^;]+;base64,(.+)$/);
                  if (base64Match) {
                    resolve(base64Match[1]);
                  } else {
                    reject(new Error("Failed to extract base64 data"));
                  }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error("Failed to read blob URL:", error);
              toast.error("Failed to read image file");
              return;
            }
          }
        }
      }
      onSubmit(message.text || "", imageData, imageMimeType);
    },
    [onSubmit]
  );
  const handleError = useCallback((err) => {
    if (err.code === "max_file_size") {
      toast.error("File size exceeds 10MB limit");
    } else if (err.code === "accept") {
      toast.error("Please upload a valid image file");
    } else {
      toast.error(err.message);
    }
  }, []);
  return /* @__PURE__ */ jsx(ChatInput, { widgetId: "concierge-chat-input", displayName: "Chat Input", children: (styles, themeIcons, resolveColor) => {
    const hasExpandedMinHeight = styles.minHeight > 0;
    const borderRadius = isMultiline || hasAttachments || hasExpandedMinHeight ? `${styles.borderRadiusMultiLine}px` : `${styles.borderRadiusSingleLine}px`;
    return /* @__PURE__ */ jsx("div", { className: "tw:mx-auto tw:w-full", style: { maxWidth: `${styles.maxWidth}px` }, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-prompt-input",
        style: {
          borderRadius,
          border: `${styles.borderWidth}px solid ${resolveColor(styles.borderColor)}`,
          backgroundColor: resolveColor(styles.backgroundColor),
          boxShadow: "none",
          transition: "border-radius 0.15s ease-out"
        },
        children: [
          /* @__PURE__ */ jsx("style", { children: `
                .concierge-prompt-input [data-slot="input-group"] {
                  border: none !important;
                  box-shadow: none !important;
                  --tw-shadow: 0 0 #0000 !important;
                  --tw-shadow-colored: 0 0 #0000 !important;
                  background: transparent !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  min-height: 0 !important;
                  height: auto !important;
                  outline: none !important;
                }
                .concierge-prompt-input [data-slot="input-group"]:has([data-slot="input-group-control"]:focus-visible) {
                  ring: none !important;
                  --tw-ring-color: transparent !important;
                  --tw-ring-offset-width: 0 !important;
                  --tw-ring-width: 0 !important;
                }
                .concierge-prompt-input [data-slot="input-group-control"]:focus-visible {
                  outline: none !important;
                  ring: none !important;
                  --tw-ring-color: transparent !important;
                  box-shadow: none !important;
                }
                .concierge-prompt-input textarea,
                .concierge-prompt-input textarea:focus,
                .concierge-prompt-input textarea:focus-visible {
                  outline: none !important;
                  box-shadow: none !important;
                  --tw-shadow: 0 0 #0000 !important;
                  --tw-shadow-colored: 0 0 #0000 !important;
                }
                .concierge-prompt-input:focus-within {
                  border-color: ${resolveColor(styles.borderColorFocus)} !important;
                }
                .concierge-prompt-input textarea {
                  font-size: ${styles.fontSize}px !important;
                  font-weight: ${getFontWeightValue(styles.fontWeight)} !important;
                  font-family: ${styles.fontFamilyCss || "inherit"} !important;
                  color: ${resolveColor(styles.textColor)} !important;
                }
                .concierge-prompt-input textarea::placeholder {
                  color: ${resolveColor(styles.placeholderColor)} !important;
                }
                /* Ensure submit button color is respected */
                .concierge-prompt-input button[type="submit"] {
                  background-color: ${resolveColor(styles.submitButtonBgColor)} !important;
                }
                .concierge-prompt-input button[type="submit"]:hover:not(:disabled) {
                  background-color: ${resolveColor(styles.submitButtonBgColorHover)} !important;
                }
                .concierge-prompt-input button[type="submit"]:disabled {
                  background-color: ${resolveColor(styles.submitButtonBgColor)} !important;
                }
              ` }),
          /* @__PURE__ */ jsx(
            PromptInput,
            {
              onSubmit: handleSubmit,
              multiple: false,
              globalDrop: true,
              accept: "image/*",
              maxFiles: 1,
              maxFileSize: MAX_FILE_SIZE,
              onError: handleError,
              className: "tw:w-full",
              children: /* @__PURE__ */ jsxs(PromptInputBody, { className: "tw:flex tw:flex-col tw:w-full", children: [
                /* @__PURE__ */ jsx(AttachmentsRow, { onAttachmentsChange: setHasAttachments, styles }),
                /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-end tw:w-full", children: [
                  /* @__PURE__ */ jsx("div", { className: "tw:flex-1 tw:min-w-0", children: /* @__PURE__ */ jsx(
                    PromptInputTextarea,
                    {
                      ref: textareaRef,
                      placeholder,
                      disabled,
                      value,
                      onChange: (e) => onChange == null ? void 0 : onChange(e.target.value),
                      rows: 1,
                      style: {
                        minHeight: styles.minHeight > 0 ? `${styles.minHeight}px` : 0,
                        paddingTop: styles.paddingY,
                        paddingBottom: styles.paddingY,
                        paddingLeft: styles.paddingX,
                        ...maxHeight ? { maxHeight, overflowY: "auto" } : {}
                      }
                    }
                  ) }),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: "tw:flex tw:items-center tw:gap-3 tw:shrink-0",
                      style: {
                        paddingRight: styles.paddingX,
                        paddingLeft: styles.paddingX,
                        paddingBottom: styles.paddingY
                      },
                      children: [
                        styles.showAttachmentButton && /* @__PURE__ */ jsx(
                          AttachmentButton,
                          {
                            disabled: disabled || isStreaming,
                            styles,
                            resolveColor
                          }
                        ),
                        styles.showMicrophoneButton && /* @__PURE__ */ jsx(
                          MicrophoneButton,
                          {
                            disabled: disabled || isStreaming,
                            textareaRef,
                            onChange,
                            styles,
                            themeIcons,
                            resolveColor
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          SubmitButton,
                          {
                            disabled: disabled || isStreaming,
                            isStreaming,
                            styles,
                            resolveColor
                          }
                        )
                      ]
                    }
                  )
                ] })
              ] })
            }
          )
        ]
      }
    ) });
  } });
};
const AttachmentsRow = ({ onAttachmentsChange, styles }) => {
  const attachments = usePromptInputAttachments();
  useEffect(() => {
    onAttachmentsChange(attachments.files.length > 0);
  }, [attachments.files.length, onAttachmentsChange]);
  if (attachments.files.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx("div", { style: { paddingLeft: styles.paddingX, paddingRight: styles.paddingX, paddingTop: styles.paddingY }, children: /* @__PURE__ */ jsx(PromptInputAttachments, { children: (attachment) => /* @__PURE__ */ jsx(PromptInputAttachment, { data: attachment }) }) });
};
const AttachmentButton = ({ disabled, styles, resolveColor }) => {
  const attachments = usePromptInputAttachments();
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      disabled,
      onClick: () => attachments.openFileDialog(),
      "aria-label": "Attach image",
      className: "tw:p-0 tw:bg-transparent tw:border-none tw:cursor-pointer disabled:tw:opacity-50 disabled:tw:cursor-not-allowed tw:transition-colors",
      style: {
        width: styles.iconSize,
        height: styles.iconSize
      },
      children: /* @__PURE__ */ jsx(IconImageUpload, { size: styles.iconSize, style: { color: resolveColor(styles.iconColor) } })
    }
  );
};
const MicrophoneButton = ({ disabled, textareaRef, onChange, styles, themeIcons, resolveColor }) => {
  const micIconKey = styles.microphoneIcon;
  const customMicIcon = micIconKey && micIconKey !== "default" ? themeIcons[micIconKey] : null;
  return /* @__PURE__ */ jsx(
    PromptInputSpeechButton,
    {
      disabled,
      textareaRef,
      onTranscriptionChange: onChange,
      "aria-label": "Voice input",
      className: "tw:p-0 tw:bg-transparent tw:shadow-none tw:border-none tw:cursor-pointer hover:tw:bg-transparent disabled:tw:opacity-50 disabled:tw:cursor-not-allowed",
      style: {
        width: styles.iconSize,
        height: styles.iconSize
      },
      children: (isListening) => /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:cursor-pointer", children: [
        customMicIcon ? (
          // Render custom icon from theme
          /* @__PURE__ */ jsx(
            "img",
            {
              src: customMicIcon.url,
              alt: customMicIcon.name,
              style: {
                width: styles.iconSize,
                height: styles.iconSize,
                filter: isListening ? `drop-shadow(0 0 2px ${resolveColor(styles.micActiveColor)})` : void 0
              }
            }
          )
        ) : (
          // Render default IconMicrophone
          /* @__PURE__ */ jsx(
            IconMicrophone,
            {
              size: styles.iconSize,
              style: {
                color: isListening ? resolveColor(styles.micActiveColor) : resolveColor(styles.iconColor),
                width: styles.iconSize,
                height: styles.iconSize
              }
            }
          )
        ),
        isListening && /* @__PURE__ */ jsx(
          "span",
          {
            className: "tw:absolute tw:-top-0.5 tw:-right-0.5 tw:size-2 tw:rounded-full tw:animate-pulse",
            style: { backgroundColor: resolveColor(styles.micActiveColor) }
          }
        )
      ] })
    }
  );
};
const LoadingSpinner = ({ size, color }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    className: "tw:animate-spin",
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: color, strokeWidth: "3", strokeLinecap: "round", opacity: "0.25" }),
      /* @__PURE__ */ jsx("path", { d: "M12 2C6.47715 2 2 6.47715 2 12", stroke: color, strokeWidth: "3", strokeLinecap: "round" })
    ]
  }
);
const SubmitButton = ({ disabled, isStreaming = false, styles, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const backgroundColor = isHovered && !disabled ? resolveColor(styles.submitButtonBgColorHover) : resolveColor(styles.submitButtonBgColor);
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "submit",
      disabled,
      "aria-label": isStreaming ? "Loading" : "Submit",
      className: "tw:flex tw:items-center tw:justify-center tw:cursor-pointer disabled:tw:opacity-50 disabled:tw:cursor-not-allowed",
      style: {
        width: styles.submitButtonSize,
        height: styles.submitButtonSize,
        backgroundColor,
        borderRadius: styles.submitButtonBorderRadius,
        border: "none",
        transition: "background-color 0.15s ease, opacity 0.15s ease"
      },
      onMouseEnter: () => !disabled && setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      children: isStreaming ? /* @__PURE__ */ jsx(LoadingSpinner, { size: styles.submitButtonSize / 2, color: resolveColor(styles.submitButtonIconColor) }) : /* @__PURE__ */ jsx(
        IconArrowRight,
        {
          size: styles.submitButtonSize / 2,
          style: { color: resolveColor(styles.submitButtonIconColor) }
        }
      )
    }
  );
};
export {
  PromptInputConcierge
};
//# sourceMappingURL=PromptInputConcierge.js.map
